"use client";

import { Routes } from "@/api";
import {
  revalidateUserSessionAction,
  updateThemeAction,
  updateUserCurrencyAction,
} from "@/app/actions/user/user-actions";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  sidebarMenuButtonVariants,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarProvider,
  useSidebar,
} from "@/components/ui/sidebar";
import {
  CHAT_INBOX_SHEET_TRIGGER,
  getDenomosLogo,
  USER_NOTIFICATIONS_SHEET_TRIGGER,
} from "@/constants";
import { useIsDesktop } from "@/hooks/layout/use-is-desktop";
import { HasUnreadNotificationsQuery } from "@/hooks/queries/user/use-has-unread-notifications-query";
import { usePwa } from "@/hooks/use-pwa";
import { removePushRegistration } from "@/providers/push-provider";
import {
  useIsMacroEnabled,
  useIsScreenerEnabled,
  useIsSubscriptionActive,
} from "@/providers/subsription-usage-provider";
import { GtmService } from "@/services/api/gtm-service";
import { withPreventDefault } from "@/utils/with-prevent-default.ts";
import { withStopPropagation } from "@/utils/with-stop-propagation";
import {
  BellIcon,
  ChevronDownIcon,
  FileChartPieIcon,
  Globe2Icon,
  LaptopMinimalIcon,
  LogOutIcon,
  MessagesSquareIcon,
  MoonStarIcon,
  PlusIcon,
  RocketIcon,
  Settings2Icon,
  SmartphoneIcon,
  SunIcon,
  TextSearchIcon,
  TicketIcon,
} from "lucide-react";
import { signOut, useSession } from "next-auth/react";
import { useTranslations } from "next-intl";
import { useTheme } from "next-themes";
import Image from "next/image";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import React, { PropsWithChildren } from "react";
import { useDisconnect } from "wagmi";
import { useUserPreferences } from "../providers/user-preferences-provider";
import { ChatRoomCallInfo } from "./chat/room/chat-room-call-info";
import CurrencySelector from "./currency-selector";
import DesktopOnly from "./desktop-only";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "./ui/collapsible";
import { Skeleton } from "./ui/skeleton";
import { UserAvatar } from "./user/user-avatar";
import { UserInvite } from "./user/user-invite";

interface Props {
  readonly isLoggedIn: boolean;
  readonly portfolioCount: Promise<number>;
  readonly PortfolioList: React.ReactNode;
  readonly MarketList: React.ReactNode;
  readonly affiliateCode: string | null;
}

export function MainSidebar(props: PropsWithChildren<Props>) {
  const ref = React.useRef<HTMLDivElement>(null);

  return (
    <SidebarWrapper ref={ref} {...props}>
      {props.children}
      {/* <BottomNav scrollDirection={scrollDirection} /> */}
    </SidebarWrapper>
  );
}

function SidebarWrapper(
  props: React.PropsWithChildren<
    Props & { ref: React.RefObject<HTMLDivElement | null> }
  >,
) {
  const pathname = usePathname() ?? "";
  const isScreenerEnabled = useIsScreenerEnabled();
  const isPortfolioRoute = pathname.startsWith(Routes.PORTFOLIOS);

  if (!props.isLoggedIn) {
    return <div className="flex flex-1 flex-col">{props.children}</div>;
  }

  return (
    <SidebarProvider defaultOpen={!isPortfolioRoute} shortcut>
      <DenomosSidebar
        portfolioCount={props.portfolioCount}
        PortfolioList={props.PortfolioList}
        affiliateCode={props.affiliateCode}
      />
      <SidebarInset ref={props.ref} className="min-w-0">
        {props.children}
      </SidebarInset>
      {isScreenerEnabled ? (
        <DesktopOnly>
          <RightSidebar MarketList={props.MarketList} />
        </DesktopOnly>
      ) : null}
    </SidebarProvider>
  );
}

function DenomosSidebar(
  props: Pick<Props, "portfolioCount" | "affiliateCode" | "PortfolioList">,
) {
  const { data: session } = useSession();
  const { setOpenMobile } = useSidebar();
  const pathname = usePathname() ?? "-";
  const isDesktop = useIsDesktop();
  const t = useTranslations("Sidebar.left");
  const isMacroEnabled = useIsMacroEnabled();
  const isScreenerEnabled = useIsScreenerEnabled();
  const isSubActive = useIsSubscriptionActive();
  const { data: unreadNotifications } = HasUnreadNotificationsQuery.useQuery();
  const { mutate: markNotificationsAsRead } =
    HasUnreadNotificationsQuery.useMutation();

  const onListItemClick = React.useCallback(
    (url: string, cb?: () => void) => () => {
      GtmService.push({
        event: "click",
        eventCategory: "link",
        eventLabel: "link-footer",
        eventAction: url,
      });

      cb?.();

      if (isDesktop) {
        return;
      }

      setOpenMobile(false);
    },
    [setOpenMobile, isDesktop],
  );

  const unreadNotificationsCount = React.useMemo(() => {
    const notifications = unreadNotifications?.notifications;
    if (!notifications?.size) {
      return new Map();
    }

    const unreadMap = new Map();
    for (const [userId, notificationCountPerType] of Array.from(
      notifications.entries(),
    )) {
      let notificationsCount = 0;
      for (const count of Array.from(notificationCountPerType.values())) {
        notificationsCount += count;
      }
      unreadMap.set(userId, notificationsCount);
    }

    return unreadMap;
  }, [unreadNotifications?.notifications]);

  const onNotificationsClick = React.useCallback(() => {
    document.getElementById(USER_NOTIFICATIONS_SHEET_TRIGGER)?.click();
    // Notifications are in format {[userId]: {type: count}}
    // We need to flatten it to get the types
    // and pass it to the markNotificationsAsRead function
    const types = Array.from(
      unreadNotifications?.notifications.values() ?? [],
    ).flatMap((map) => Array.from(map.keys()));

    markNotificationsAsRead({ types });
  }, [markNotificationsAsRead, unreadNotifications?.notifications]);

  const getSidebarButtonStyle = React.useCallback((isActive: boolean) => {
    return sidebarMenuButtonVariants({
      className: isActive
        ? "bg-sidebar-accent text-sidebar-accent-foreground"
        : "",
    });
  }, []);

  return (
    <Sidebar variant="inset">
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarTriggerLogo />
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>
      <SidebarContent>
        <SidebarMenu className="px-2">
          <SidebarMenuButton asChild tooltip={t("portfolio.create")}>
            <Link
              href={Routes.PORTFOLIO_CREATE}
              className={getSidebarButtonStyle(
                pathname.startsWith(Routes.PORTFOLIO_CREATE),
              )}
              prefetch={false}
            >
              <PlusIcon className="size-4" />
              {t("portfolio.create")}
            </Link>
          </SidebarMenuButton>
          {isMacroEnabled ? (
            <SidebarMenuButton asChild tooltip={t("macroEconomy")}>
              <Link
                href={Routes.MACRO_ECONOMY}
                onClick={onListItemClick(Routes.MACRO_ECONOMY)}
                className={getSidebarButtonStyle(
                  pathname.startsWith(Routes.MACRO_ECONOMY),
                )}
                prefetch
              >
                <Globe2Icon className="size-4" />
                {t("macroEconomy")}
              </Link>
            </SidebarMenuButton>
          ) : null}
          {isScreenerEnabled ? (
            <SidebarMenuButton asChild tooltip={t("screener")}>
              <Link
                href={Routes.SCREENER}
                onClick={onListItemClick(Routes.SCREENER)}
                className={getSidebarButtonStyle(
                  pathname.startsWith(Routes.SCREENER),
                )}
                prefetch
              >
                <TextSearchIcon className="size-4" />
                {t("screener")}
              </Link>
            </SidebarMenuButton>
          ) : null}
          <Collapsible defaultOpen className="group/collapsible">
            <SidebarGroup className="p-0">
              <React.Suspense fallback={<Skeleton className="h-12 w-full" />}>
                <CreatePortfolioTitle count={props.portfolioCount} />
              </React.Suspense>
              <CollapsibleContent>
                <SidebarGroupContent className="pl-2">
                  {props.PortfolioList}
                </SidebarGroupContent>
              </CollapsibleContent>
            </SidebarGroup>
          </Collapsible>
          <Collapsible className="group/collapsible">
            <SidebarMenuItem>
              <CollapsibleTrigger asChild>
                <SidebarMenuButton
                  className={getSidebarButtonStyle(
                    pathname.startsWith(Routes.SUBSCRIPTIONS),
                  )}
                  tooltip={t("subscriptions.item")}
                >
                  <TicketIcon className="size-4" />
                  {t("subscriptions.item")}
                  <ChevronDownIcon className="ml-auto transition-transform group-data-[state=open]/collapsible:rotate-180" />
                </SidebarMenuButton>
              </CollapsibleTrigger>
            </SidebarMenuItem>
            <CollapsibleContent>
              <SidebarMenuSub>
                <SidebarMenuSubItem>
                  <SidebarMenuSubButton asChild>
                    <Link
                      href={
                        isSubActive
                          ? Routes.SUBSCRIPTIONS_OVERVIEW
                          : Routes.PRICING
                      }
                      prefetch={isSubActive}
                      className={getSidebarButtonStyle(
                        pathname.startsWith(Routes.SUBSCRIPTIONS_OVERVIEW),
                      )}
                      onClick={onListItemClick(Routes.SUBSCRIPTIONS_OVERVIEW)}
                    >
                      {t("subscriptions.subItems.overview")}
                    </Link>
                  </SidebarMenuSubButton>
                </SidebarMenuSubItem>
                <SidebarMenuSubItem>
                  <SidebarMenuSubButton asChild>
                    <Link
                      href={
                        isSubActive
                          ? Routes.SUBSCRIPTIONS_PLANS
                          : Routes.PRICING
                      }
                      prefetch={isSubActive}
                      className={getSidebarButtonStyle(
                        pathname.startsWith(Routes.SUBSCRIPTIONS_PLANS),
                      )}
                      onClick={onListItemClick(Routes.SUBSCRIPTIONS_PLANS)}
                    >
                      {t("subscriptions.subItems.plans")}
                    </Link>
                  </SidebarMenuSubButton>
                </SidebarMenuSubItem>
                <SidebarMenuSubItem>
                  <SidebarMenuSubButton asChild>
                    <Link
                      href={
                        isSubActive
                          ? Routes.SUBSCRIPTIONS_BILLING
                          : Routes.PRICING
                      }
                      prefetch={isSubActive}
                      className={getSidebarButtonStyle(
                        pathname.startsWith(Routes.SUBSCRIPTIONS_BILLING),
                      )}
                      onClick={onListItemClick(Routes.SUBSCRIPTIONS_BILLING)}
                    >
                      {t("subscriptions.subItems.billing")}
                    </Link>
                  </SidebarMenuSubButton>
                </SidebarMenuSubItem>
                <SidebarMenuSubItem>
                  <SidebarMenuSubButton asChild>
                    <Link
                      href={
                        isSubActive
                          ? Routes.SUBSCRIPTIONS_SETTINGS
                          : Routes.PRICING
                      }
                      prefetch={isSubActive}
                      className={getSidebarButtonStyle(
                        pathname.startsWith(Routes.SUBSCRIPTIONS_SETTINGS),
                      )}
                      onClick={onListItemClick(Routes.SUBSCRIPTIONS_SETTINGS)}
                    >
                      {t("subscriptions.subItems.settings")}
                    </Link>
                  </SidebarMenuSubButton>
                </SidebarMenuSubItem>
              </SidebarMenuSub>
            </CollapsibleContent>
          </Collapsible>
          {/* <SidebarMenuButton asChild tooltip={t("affiliate")}>
            <Link
              href={Routes.AFFILIATE}
              className={getSidebarButtonStyle(
                pathname.startsWith(Routes.AFFILIATE),
              )}
              onClick={onListItemClick(Routes.AFFILIATE)}
            >
              <UserPlusIcon />
              {t("affiliate")}
            </Link>
          </SidebarMenuButton> */}
          <SidebarMenuButton
            onClick={onNotificationsClick}
            tooltip={t("notifications")}
            className="relative"
          >
            <BellIcon />
            {t("notifications")}
            {unreadNotificationsCount?.get(session?.user.id) ? (
              <span className="absolute left-5 top-1 flex size-2 animate-pulse rounded-full bg-red-500" />
            ) : null}
          </SidebarMenuButton>
          {/* <SidebarMenuButton asChild tooltip={t("featureRequest")}>
            <Link
              href={Routes.FEEDBACK}
              className={getSidebarButtonStyle(
                pathname.startsWith(Routes.FEEDBACK),
              )}
              onClick={onListItemClick(Routes.FEEDBACK)}
              target="_blank"
            >
              <HeartHandshakeIcon />
              {t("featureRequest")}
            </Link>
          </SidebarMenuButton> */}
        </SidebarMenu>
      </SidebarContent>
      <SidebarFooter>
        {session ? (
          <SidebarMenu>
            <DesktopOnly>
              <SidebarMenuItem>
                <ChatRoomCallInfo className="w-full items-center justify-center gap-2 pb-2 [&>svg]:h-[4px]" />
              </SidebarMenuItem>
            </DesktopOnly>
            <SidebarMenuItem>
              <DenomosDropdown onListItemClick={onListItemClick} {...props} />
            </SidebarMenuItem>
          </SidebarMenu>
        ) : null}
      </SidebarFooter>
      {/* <SidebarRail /> */}
    </Sidebar>
  );
}

function DenomosDropdown(
  props: Pick<Props, "portfolioCount" | "affiliateCode" | "PortfolioList"> & {
    onListItemClick: (url: string, cb?: () => void) => () => void;
  },
) {
  const pwa = usePwa();
  const router = useRouter();
  const { theme, setTheme } = useTheme();
  const t = useTranslations("Sidebar.left");
  const { data: session } = useSession();
  const { disconnect } = useDisconnect();
  const pathname = usePathname() ?? "";
  const userPreferences = useUserPreferences();
  const isInboxRoute = pathname.startsWith(Routes.INBOX);
  const isDesktop = useIsDesktop();
  const hideChats = isInboxRoute && isDesktop;

  const logout = React.useCallback(async () => {
    disconnect();
    await signOut({
      redirect: true,
      callbackUrl: Routes.HOME,
    });
    await removePushRegistration();
    await revalidateUserSessionAction();
  }, [disconnect]);

  const routeTo = React.useCallback(
    (url: string) => () => {
      router.push(url);
    },
    [router],
  );

  const toggleTheme = React.useCallback(async () => {
    let newTheme = "light";
    if (theme === "light") {
      newTheme = "dark";
    }
    setTheme(newTheme);
    await updateThemeAction(newTheme);
  }, [setTheme, theme]);

  if (!session) {
    return null;
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <SidebarMenuButton
          size="lg"
          className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
        >
          <UserAvatar user={session.user} />
          <div className="grid flex-1 text-left text-sm leading-tight">
            <span className="truncate font-semibold">{session.user.name}</span>
          </div>
        </SidebarMenuButton>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        className="z-40 w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
        side={isDesktop ? "right" : "bottom"}
        align="end"
        sideOffset={4}
      >
        <DropdownMenuLabel className="p-0 font-normal">
          <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
            <UserAvatar user={session.user} />
            <div className="grid flex-1 text-left text-sm leading-tight">
              <span className="truncate font-semibold">
                {session.user.name}
              </span>
            </div>
          </div>
          <CurrencySelector
            className="w-full px-2"
            onChange={updateUserCurrencyAction}
            code={userPreferences.currency ?? undefined}
          />
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {/* <DropdownMenuGroup>
                    
                    <DropdownMenuItem className="gap-2">
                      <SparklesIcon className="size-4" />
                      Upgrade to Pro
                    </DropdownMenuItem>
                  </DropdownMenuGroup>
                  <DropdownMenuSeparator /> */}
        <DropdownMenuGroup>
          {/* <DropdownMenuItem className="gap-2">
                      <BadgeCheckIcon className="size-4" />
                      Account
                    </DropdownMenuItem>
                    <DropdownMenuItem className="gap-2">
                      <CreditCardIcon className="size-4" />
                      Billing
                    </DropdownMenuItem> */}
          {pwa.installPrompt ? (
            <DropdownMenuItem className="gap-2" onClick={pwa.onInstallClick}>
              {isDesktop ? (
                <LaptopMinimalIcon className="size-4" />
              ) : (
                <SmartphoneIcon className="size-4" />
              )}
              {t("dropdown.installApp")}
            </DropdownMenuItem>
          ) : null}

          {props.affiliateCode ? (
            <UserInvite code={props.affiliateCode} />
          ) : null}

          {!hideChats && (
            <DropdownMenuItem
              className="gap-2"
              onClick={() => {
                document.getElementById(CHAT_INBOX_SHEET_TRIGGER)?.click();
              }}
            >
              <MessagesSquareIcon className="size-4" />
              {t("dropdown.chat")}
            </DropdownMenuItem>
          )}
          <DropdownMenuItem
            className="gap-2"
            onClick={withPreventDefault(withStopPropagation(toggleTheme))}
          >
            <SunIcon className="size-4 rotate-0 scale-100 transition-all dark:-rotate-90 dark:scale-0" />
            <MoonStarIcon className="absolute size-4 rotate-90 scale-0 transition-all dark:rotate-0 dark:scale-100" />
            {t("dropdown.theme")}
          </DropdownMenuItem>
          {/* <DropdownMenuItem asChild>
            <ConnectDrawdownMenuItem showDisconnect />
          </DropdownMenuItem> */}
          <DropdownMenuItem
            className="gap-2"
            onClick={props.onListItemClick(
              Routes.USER_PORTFOLIOS(session.user.id),
              routeTo(Routes.SETTINGS_PROFILE),
            )}
          >
            <Settings2Icon className="size-4" />
            {t("dropdown.settings")}
          </DropdownMenuItem>
        </DropdownMenuGroup>
        <DropdownMenuSeparator />
        <DropdownMenuItem className="gap-2" onClick={logout}>
          <LogOutIcon className="size-4" />
          {t("dropdown.logout")}
        </DropdownMenuItem>
        <DropdownMenuItem className="gap-2">
          <RocketIcon className="size-4" />
          {t("dropdown.version", {
            version: process.env.NEXT_PUBLIC_APP_VERSION,
          })}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function SidebarTriggerLogo() {
  const { theme } = useTheme();
  const t = useTranslations("metadata");

  return (
    <div
      className={sidebarMenuButtonVariants({
        size: "lg",
      })}
    >
      <Image
        src={getDenomosLogo(theme)}
        width={28}
        height={28}
        className="aspect-square size-6"
        alt="Denomos Logo"
      />
      <Link
        className="grid flex-1 text-left text-lg leading-tight"
        href={Routes.HOME}
        aria-label="Denomos"
      >
        <span className="truncate font-semibold">{t("name")}</span>
        {/* <span className="truncate text-xs">Subscription plan goes here</span> */}
      </Link>
    </div>
  );
}

function CreatePortfolioTitle({ count }: { count: Promise<number> }) {
  const t = useTranslations("Portfolio");
  const portfolioCount = React.use(count);

  return (
    <SidebarGroupLabel
      asChild
      className={`${sidebarMenuButtonVariants()} text-foreground`}
    >
      <CollapsibleTrigger disabled={portfolioCount === 0}>
        <FileChartPieIcon />
        {t("plural", { count: portfolioCount })} ({portfolioCount})
        <ChevronDownIcon className="ml-auto transition-transform group-data-[state=open]/collapsible:rotate-180" />
      </CollapsibleTrigger>
    </SidebarGroupLabel>
  );
}

function RightSidebar(props: { MarketList: React.ReactNode }) {
  const t = useTranslations("Sidebar.right");

  return (
    <Sidebar side="right" className="hidden border-none lg:flex">
      <SidebarHeader>
        <Link href={Routes.SCREENER} className="flex items-center gap-2 p-2">
          <TextSearchIcon className="size-4" />
          <h3 className="font-semibold">{t("title")}</h3>
        </Link>
      </SidebarHeader>
      <SidebarContent className="px-2">{props.MarketList}</SidebarContent>
      {/* <SidebarFooter className="border-t p-4">
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="font-medium">Notification Settings</h4>
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label htmlFor="price-alerts" className="text-sm">
                Price Alerts
              </label>
              <Switch id="price-alerts" defaultChecked />
            </div>
            <div className="flex items-center justify-between">
              <label htmlFor="signal-alerts" className="text-sm">
                Signal Alerts
              </label>
              <Switch id="signal-alerts" defaultChecked />
            </div>
            <div className="flex items-center justify-between">
              <label htmlFor="news-alerts" className="text-sm">
                News Alerts
              </label>
              <Switch id="news-alerts" />
            </div>
          </div>
        </div>
      </SidebarFooter> */}
    </Sidebar>
  );
}
